// Generates a square (1080x1080) shareable score card and shares it via the
// Web Share API (WhatsApp/Instagram/etc.), falling back to a PNG download.

import { buildChallengeUrl } from "@/lib/challenge";

const SHARE_URL = "nicabros.com";
const LOGO_SRC = `${process.env.PUBLIC_URL || ""}/game-assets/nicabros-logo.webp`;

let _logoPromise = null;
function loadLogo() {
  if (_logoPromise) return _logoPromise;
  _logoPromise = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = LOGO_SRC;
  });
  return _logoPromise;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export async function renderScoreCard({ score, characterName, levelName, charColor = "#FFEB3B", lang = "en" }) {
  const S = 1080;
  const c = document.createElement("canvas");
  c.width = S; c.height = S;
  const ctx = c.getContext("2d");

  // Background — deep Nicaragua blue with a darker vignette
  const g = ctx.createLinearGradient(0, 0, 0, S);
  g.addColorStop(0, "#0a3d7a");
  g.addColorStop(1, "#041d3d");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, S, S);

  // subtle frame
  ctx.strokeStyle = "rgba(255,235,59,0.55)";
  ctx.lineWidth = 10;
  roundRect(ctx, 28, 28, S - 56, S - 56, 36);
  ctx.stroke();

  ctx.textAlign = "center";

  // Brand logo (rounded badge) at the top
  const logo = await loadLogo();
  const LW = 360, LX = (S - LW) / 2, LY = 60;
  if (logo) {
    ctx.save();
    roundRect(ctx, LX, LY, LW, LW, 32);
    ctx.clip();
    ctx.drawImage(logo, LX, LY, LW, LW);
    ctx.restore();
    ctx.strokeStyle = "rgba(255,235,59,0.8)";
    ctx.lineWidth = 6;
    roundRect(ctx, LX, LY, LW, LW, 32);
    ctx.stroke();
  } else {
    ctx.fillStyle = "#FFEB3B";
    ctx.font = "900 70px Arial, sans-serif";
    ctx.fillText("NICA BROS  🇳🇮", S / 2, 200);
  }

  ctx.fillStyle = "rgba(255,255,255,0.9)";
  ctx.font = "600 40px Arial, sans-serif";
  ctx.fillText(lang === "en" ? "I just scored" : "Acabo de hacer", S / 2, 510);

  // Big score
  ctx.fillStyle = charColor;
  ctx.font = "900 185px Arial, sans-serif";
  ctx.fillText(Number(score).toLocaleString(), S / 2, 685);

  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.font = "600 42px Arial, sans-serif";
  ctx.fillText("pts", S / 2, 745);

  // Character + level pill
  ctx.fillStyle = "rgba(0,0,0,0.45)";
  roundRect(ctx, S / 2 - 390, 785, 780, 100, 24);
  ctx.fill();
  ctx.fillStyle = "#fff";
  ctx.font = "700 40px Arial, sans-serif";
  const who = lang === "en" ? `as ${characterName}` : `con ${characterName}`;
  ctx.fillText(`${who} · ${levelName}`, S / 2, 850);

  // CTA
  ctx.fillStyle = "#FF5722";
  roundRect(ctx, S / 2 - 430, 915, 860, 130, 30);
  ctx.fill();
  ctx.fillStyle = "#fff";
  ctx.font = "900 46px Arial, sans-serif";
  ctx.fillText(lang === "en" ? "Can you beat me?" : "¿Podés superarme?", S / 2, 975);
  ctx.font = "700 38px Arial, sans-serif";
  ctx.fillText(`▶ ${SHARE_URL}`, S / 2, 1025);

  return new Promise((resolve) => c.toBlob((b) => resolve(b), "image/png"));
}

// Generate the score card as an object URL for an inline preview thumbnail.
export async function renderScoreCardUrl(opts) {
  try {
    const blob = await renderScoreCard(opts);
    return blob ? URL.createObjectURL(blob) : null;
  } catch (e) {
    return null;
  }
}

export async function shareScoreCard(opts) {
  const lang = opts.lang || "en";
  let link = SHARE_URL.startsWith("http") ? SHARE_URL : `https://${SHARE_URL}`;
  // If we have enough config, build a "beat my score" challenge deep link.
  if (opts.charId && typeof opts.levelIndex === "number") {
    link = buildChallengeUrl({
      levelIndex: opts.levelIndex,
      charId: opts.charId,
      diffId: opts.diffId || "normal",
      score: opts.score,
      name: opts.playerName || "",
    });
  }
  const score = Number(opts.score).toLocaleString();
  const text = lang === "en"
    ? `🎮 I scored ${score} in Nica Bros 🇳🇮 — can you beat me? Play free: ${link}`
    : `🎮 Hice ${score} puntos en Nica Bros 🇳🇮 — ¿podés superarme? Jugá gratis: ${link}`;
  let blob;
  try { blob = await renderScoreCard(opts); } catch (e) { blob = null; }

  // Best case: native share with the image AND the playable link (url + text).
  if (blob && navigator.canShare) {
    const file = new File([blob], "nicabros-score.png", { type: "image/png" });
    if (navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ title: "Nica Bros 🇳🇮", text, url: link, files: [file] });
        return "shared";
      } catch (e) { if (e?.name === "AbortError") return "cancelled"; }
    }
  }
  // Link-first share (no image) — guarantees a clickable, playable link.
  if (navigator.share) {
    try { await navigator.share({ title: "Nica Bros 🇳🇮", text, url: link }); return "shared"; }
    catch (e) { if (e?.name === "AbortError") return "cancelled"; }
  }
  // Desktop / no Web Share: copy the playable link to clipboard, then save the card.
  try { await navigator.clipboard.writeText(link); } catch (e) { /* noop */ }
  if (blob) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "nicabros-score.png"; a.click();
    URL.revokeObjectURL(url);
    return "downloaded";
  }
  return "failed";
}
